import type { ReactNode } from 'react'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import styled from 'styled-components'
import { rem } from 'polished'
import TripDrawer from 'tripPlanner/components/GenericDrawer/TripDrawer'
import TripDrawerHeaderTitle from 'tripPlanner/components/GenericDrawer/TripDrawerHeaderTitle'
import {
  useRightPanelTripDrawerPresentation,
  type TripDrawerPresentation,
} from 'tripPlanner/components/GenericDrawer/TripDrawerPresentationContext'
import ModalProvider from 'contexts/ModalProvider'
import ModalContext from 'contexts/ModalContext'
import FlightSearchWidgetStateProvider from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateProvider'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import flightSearchWidgetStateReducer, {
  createEmptyFlightSearchWidgetFlightItem,
  FlightSearchWidgetActions,
  initialState,
} from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import type {
  FlightSearchWidgetState,
  FlightSearchWidgetDispatchAction,
} from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import type { FlightSearchWidgetInitialFocus } from 'contexts/Flights/FlightSearchWidget/types'
import FlightSearchWidgetDesktop from 'components/Flights/FlightsSearch/SearchWidget/FlightSearchWidgetDesktop'
import FlightSearchWidgetMobile from 'components/Flights/FlightsSearch/SearchWidget/FlightSearchWidgetMobile'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import useSearchFlights from 'hooks/useSearchFlights'
import type { FlightSearchParams } from 'hooks/useSearchFlights'
import {
  FlightSegment,
  FlightSortOptions,
  FlightsFareTypes,
  fareClassToUrlMap,
  parseFareClassFromUrl,
  FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES,
} from 'constants/flight'
import { INTL_DAY_MONTH_NAME, ISO_DATE_FORMAT } from 'constants/dateFormats'
import {
  resetFlightsTimer,
  hasFlightSessionExpired,
} from 'components/Flights/FlightsSessionTimer/FlightsSessionTimer'
import { FlightSessionCountdownClock } from 'components/Common/FlightSessionCountdownClock/FlightSessionCountdownClock'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import type { TransportOption } from 'tripPlanner/api/types'
import { useAppSelector } from 'hooks/reduxHooks'
import GeoContext from 'contexts/geoContext'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { cacheShortlistedJourneys } from 'tripPlanner/pages/chat/ChatTripPlannerPage/components/ShortlistedFlightsSection/shortlistedJourneyCache'
import { selectFlightFareFamilies } from 'tripPlanner/components/Chat/Flights/selectFlightFareFamilies'
import ChatFlightResultsHeader from 'tripPlanner/components/Chat/Flights/ChatFlightResultsHeader'
import { buildFlightContext } from 'tripPlanner/components/Chat/Flights/buildFlightContext'
import useChatFlightResultsSorting from 'tripPlanner/components/Chat/Flights/useChatFlightResultsSorting'
import {
  getSegmentKey,
  getSegmentKeys,
  getSearchItinerary,
} from 'tripPlanner/components/Chat/Flights/flightSegmentUtils'
import { buildMultiCityFlightMessage } from 'tripPlanner/components/Chat/Flights/multiCityFlightMessage'
import { useTripFlightFilters } from 'tripPlanner/components/Chat/Flights/useTripFlightFilters'
import TripFlightFiltersPanel from 'tripPlanner/components/Chat/Flights/TripFlightFiltersPanel'
import { resolveSearchCalendarFocusDate } from 'tripPlanner/utils/searchCalendarFocus'
import TripDrawerAssistantBridge from '../TripDrawerAssistantBridge'
import { buildFlightSearchDrawerContext } from '../../utils/drawerAskAIContext'
import FlightSearchResultCard from './FlightSearchResultCard'
import FlightJourneyDetailsDrawer from './FlightJourneyDetailsDrawer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import AssistChip from 'LuxKit/Chips/AssistChip'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FlightsLoadingTopBar from 'components/Flights/V2/FlightsLoadingTopBar'
import LoadingAnimationBox from 'components/Common/LoadingAnimationBox/LoadingAnimationBox'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import StepperProgressSteps from 'LuxKit/Steppers/StepperProgressSteps'
import StepperProgressStep from 'LuxKit/Steppers/StepperProgressStep'
import type { StepClickHandler } from 'LuxKit/Steppers/StepperProgressStep'
import uuidV4 from 'lib/string/uuidV4Utils'
import { getInitialFlightSearchFocus } from './initialFlightSearchFocus'
import { getValidFlightSearchDate } from './flightSearchDateUtils'
import usePersistTripChatRecentSearch from 'tripPlanner/hooks/usePersistTripChatRecentSearch'
import { useTripPlannerEventBusOptional } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { buildFlightSearchSubmittedActivityEntry } from './buildFlightSearchSubmittedActivityEntry'
import { buildLastFlightSearchCommittedStateEntry } from './buildLastFlightSearchCommittedStateEntry'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { buildFlightRecentSearchUpsertBody } from 'tripPlanner/utils/tripChatRecentSearchPayload'
import LineExternalLinkAltIcon from 'LuxKit/Icons/line/LineExternalLinkAltIcon'
import { buildStandaloneFlightSearchUrl, type StandaloneFlightSearchParam } from 'lib/flights/flightUtils'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import { drawerBodyInlinePaddingStyles } from '../drawerBodyLayout.styles'

const SearchWrapper = styled.div`
  padding-block: ${rem(20)};
  ${drawerBodyInlinePaddingStyles}
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;

  ${mediaQueryUp.tablet} {
    padding-block: ${rem(12)};
  }

  /*
    The shared airport field (SearchFormTextField) covers its own input with an opaque
    display overlay inset by 1px. That fill lands on the input's 1px border and, at
    fractional device pixel ratios, washes it out to near-white — the From/To fields lose
    their outer edges while the neighbouring date fields, which have no overlay, keep
    theirs. Let the overlay draw text only, and hide the input beneath it so nothing shows
    through. Scoped here rather than in the shared component, so only this drawer changes.
  */
  .display-field {
    background-color: transparent;
  }

  span:has(+ .display-field):not(:focus-within) > input {
    color: transparent;

    &::placeholder {
      color: transparent;
    }
  }
`

const ContentWrapper = styled.div`
  padding-block-start: ${rem(12)};
  padding-block-end: ${rem(12)};
  ${drawerBodyInlinePaddingStyles}
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;

  ${mediaQueryUp.tablet} {
    padding-block-start: ${rem(24)};
    padding-block-end: ${rem(24)};
  }
`

const CountdownWrapper = styled.div`
  padding-block: ${rem(8)};
  ${drawerBodyInlinePaddingStyles}
  box-sizing: border-box;
`

const MobileSearchForm = styled(VerticalSpacer)`
  ${mediaQueryDown.mobile} {
    align-items: flex-end;
  }
`

const MobileSearchButton = styled(TextButton)`
  ${mediaQueryDown.mobile} {
    min-width: ${rem(80)};
  }
`

const EMPTY_ITINERARY = [
  { departureAirport: '', arrivalAirport: '', departureDate: '' },
]

const EMPTY_SEARCH_PARAMS: FlightSearchParams = {
  itinerary: EMPTY_ITINERARY,
  occupancies: [{ adults: 1 }],
  fareClass: 'economy',
}

interface FlightSearchDefaults extends Omit<Pick<Props, 'originAirportCode' | 'destinationAirportCode' | 'departureDate' | 'returnDate' | 'fareType'>, 'itinerary'> {
  itinerary?: Array<Partial<FlightSearchParams['itinerary'][number]>>
}

export function hasCompleteFlightSearchDefaults({
  originAirportCode,
  destinationAirportCode,
  departureDate,
  returnDate,
  itinerary,
  fareType,
}: FlightSearchDefaults): boolean {
  if (itinerary?.length && fareType === FlightsFareTypes.MULTI_CITY) {
    return itinerary.every(leg => Boolean(leg.departureAirport && leg.arrivalAirport && leg.departureDate))
  }

  if (fareType === FlightsFareTypes.RETURN) {
    return Boolean(originAirportCode && destinationAirportCode && departureDate && returnDate)
  }

  return Boolean(originAirportCode && destinationAirportCode && departureDate)
}

interface Props {
  isOpen: boolean
  onClose: () => void
  originAirportCode?: string
  originAirportName?: string
  destinationAirportCode?: string
  destinationAirportName?: string
  departureDate?: string
  returnDate?: string
  /**
   * The itinerary's loose `YYYY-MM` travel-month hint, used to pick which month
   * the date pickers open on when no departure date is known.
   */
  indicativeSearchMonth?: string
  itinerary?: FlightSearchParams['itinerary']
  fareType?: string
  fareClass?: string
  occupants?: App.Occupants
  passengers?: number
  autoSearch?: boolean
  onSendMessage?: (message: string) => void
  isStreaming?: boolean
  onCartFlightSelect?: (
    selection: FlightSearchDrawerCartSelection,
  ) => Promise<boolean | void> | boolean | void
  isCartSubmitting?: boolean
  existingFlights?: Array<TransportOption>
  /** Rendered under the "Saved" tab of the drawer (e.g. shortlisted flights). */
  headerSlot?: ReactNode
  /** Whether there are saved/shortlisted flights to surface under the "Saved" tab. */
  hasSavedFlights?: boolean
  showMultiCity?: boolean
  tripStopId?: number | null
}

export interface FlightSearchDrawerCartSelection {
  departingJourney: App.JourneyV2
  returningJourney?: App.JourneyV2
  multiCityJourneys?: Array<App.JourneyV2>
  searchParams: FlightSearchParams
  searchId: string | undefined
  sessionId: string | undefined
  searchExpiry: number
}

interface ContentProps {
  onSendMessage?: (message: string) => void
  isStreaming: boolean
  onCartFlightSelect?: (
    selection: FlightSearchDrawerCartSelection,
  ) => Promise<boolean | void> | boolean | void
  isCartSubmitting: boolean
  onClose: () => void
  existingFlightFingerprints: Set<string>
  autoSearch?: boolean
  headerSlot?: ReactNode
  hasSavedFlights?: boolean
  showMultiCity?: boolean
  drawerPresentation: TripDrawerPresentation
  desktopInitialFocus?: FlightSearchWidgetInitialFocus
  mobileInitialFocus?: FlightSearchWidgetInitialFocus
  tripStopId?: number | null
}

function getFlightFingerprint(
  flightNumber: string | undefined,
  departureAirport: string | undefined,
  arrivalAirport: string | undefined,
): string {
  return `${flightNumber ?? ''}|${departureAirport ?? ''}|${
    arrivalAirport ?? ''
  }`
}

function isJourneyAlreadyAdded(
  journey: App.JourneyV2,
  fingerprints: Set<string>,
): boolean {
  const flights = journey.flightGroup.flights
  const firstFlight = flights[0]
  const lastFlight = flights[flights.length - 1]
  return fingerprints.has(
    getFlightFingerprint(
      firstFlight.flightNumber,
      firstFlight.departureAirport,
      lastFlight.arrivalAirport,
    ),
  )
}

function buildFlightSearchIntentParams({
  flights,
  occupants,
  fareType,
  fareCabin,
}: Pick<FlightSearchWidgetState, 'flights' | 'occupants' | 'fareType' | 'fareCabin'>): FlightSearchParams {
  return {
    itinerary: getSearchItinerary(flights, fareType?.value) ?? EMPTY_ITINERARY,
    occupancies: [occupants],
    fareClass: fareCabin?.value
      ? fareClassToUrlMap[fareCabin.value] ?? 'economy'
      : 'economy',
    fareType: fareType?.value,
  }
}

function buildFlightSearchContextLabel(params: FlightSearchParams | undefined): string {
  const itinerary = params?.itinerary ?? EMPTY_ITINERARY
  const firstLeg = itinerary[0]
  const lastLeg = itinerary[itinerary.length - 1]
  const from = firstLeg?.departureAirport
  const to = lastLeg?.arrivalAirport

  if (from && to) {
    return `${from} to ${to} flights`
  }

  return 'Flights'
}

function buildStandaloneSearchParams(state: FlightSearchWidgetState): StandaloneFlightSearchParam | undefined {
  const firstFlight = state.flights[0]
  const fareType = state.fareType?.value
  if (!firstFlight?.departureAirport?.airportCode || !firstFlight.arrivalAirport?.airportCode || !firstFlight.checkinDate || !fareType || fareType === FlightsFareTypes.MULTI_CITY) {
    return undefined
  }
  if (fareType === FlightsFareTypes.RETURN && !firstFlight.checkoutDate) {
    return undefined
  }

  const occupancy = state.occupants

  return {
    originAirportCode: firstFlight.departureAirport.airportCode,
    originAirportName: firstFlight.departureAirport.airportName,
    destinationAirportCode: firstFlight.arrivalAirport.airportCode,
    destinationAirportName: firstFlight.arrivalAirport.airportName,
    departDate: firstFlight.checkinDate.format(ISO_DATE_FORMAT),
    returnDate: fareType === FlightsFareTypes.RETURN && firstFlight.checkoutDate ? firstFlight.checkoutDate.format(ISO_DATE_FORMAT) : null,
    adults: occupancy.adults,
    children: occupancy.children ?? 0,
    infants: occupancy.infants ?? 0,
    childrenAge: occupancy.childrenAge?.join(','),
    fareClass: state.fareCabin?.value ? fareClassToUrlMap[state.fareCabin.value] ?? 'economy' : 'economy',
    fareType: fareType === FlightsFareTypes.RETURN ? 'return' : 'oneWay',
  }
}

function FlightSearchDrawerContent({
  onSendMessage,
  onCartFlightSelect,
  isCartSubmitting,
  onClose,
  existingFlightFingerprints,
  autoSearch,
  headerSlot,
  hasSavedFlights,
  showMultiCity,
  isStreaming,
  drawerPresentation,
  desktopInitialFocus,
  mobileInitialFocus,
  tripStopId,
}: ContentProps) {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const { chatId } = useChatStreamStatus()
  const persistRecentSearch = usePersistTripChatRecentSearch(chatId)
  const eventBus = useTripPlannerEventBusOptional()
  const isDesktopSearchWidgetActive = useScreenSizeUp('tablet')
  const isCartMode = !!onCartFlightSelect
  const canSelectFlight = isCartMode || !!onSendMessage
  const shouldUseMobileDetailsDrawer = !isDesktopSearchWidgetActive
  const isResultSelectionDisabled = isCartSubmitting || (shouldUseMobileDetailsDrawer && isStreaming)
  const [searchParams, setSearchParams] = useState<
    FlightSearchParams | undefined
  >(undefined)
  const [hasSearched, setHasSearched] = useState(false)
  const [searchVersion, setSearchVersion] = useState(0)

  const [activeSegmentKey, setActiveSegmentKey] = useState<string>(
    FlightSegment.DEPARTING,
  )
  const [flightSort, setFlightSort] = useState<FlightSortOptions>(
    FlightSortOptions.Best,
  )
  const [selectedFlights, setSelectedFlights] = useState<
    Record<string, App.JourneyV2>
  >({})
  const [selectedFareFamilies, setSelectedFareFamilies] = useState<
    Record<string, App.FlightFareFamily>
  >({})
  const [detailsJourney, setDetailsJourney] = useState<App.JourneyV2 | undefined>(undefined)
  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false)

  const widgetState = useContext(FlightSearchWidgetStateContext)
  const showModal = useContext(ModalContext)
  const { currentRegionCode } = useContext(GeoContext)
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const canRequestLuxPlusFlightPricing = canRedeemLuxPlusBenefits && currentRegionCode === 'AU'
  const { flights, occupants, fareType, fareCabin } = widgetState

  const isReturn = fareType?.value === FlightsFareTypes.RETURN
  const isMultiCity = fareType?.value === FlightsFareTypes.MULTI_CITY
  const defaultSegmentCount = isReturn ? 2 : Math.max(1, flights.length)
  const segments = useMemo<Array<string>>(
    () =>
      getSegmentKeys(
        searchParams?.itinerary.length ?? defaultSegmentCount,
        isReturn,
      ),
    [defaultSegmentCount, isReturn, searchParams?.itinerary.length],
  )
  const activeSegment = segments.includes(activeSegmentKey)
    ? activeSegmentKey
    : segments[0] ?? activeSegmentKey
  const isReturningStep = activeSegmentKey === FlightSegment.RETURNING

  const departDate = useMemo(() => {
    const flight = flights[0]
    return flight?.checkinDate
      ? moment(flight.checkinDate).format(ISO_DATE_FORMAT)
      : undefined
  }, [flights])

  const returnDate = useMemo(() => {
    const flight = flights[0]
    return flight?.checkoutDate
      ? moment(flight.checkoutDate).format(ISO_DATE_FORMAT)
      : undefined
  }, [flights])

  const handleSearch = useCallback((isExplicitSubmit: boolean = false) => {
    const itinerary = getSearchItinerary(flights, fareType?.value)
    if (!itinerary) {
      return
    }

    const nextVersion = searchVersion + 1
    const params: FlightSearchParams = {
      itinerary,
      occupancies: [occupants],
      fareClass: fareCabin?.value
        ? fareClassToUrlMap[fareCabin.value] ?? 'economy'
        : 'economy',
      fareType: fareType?.value,
      key: nextVersion,
    }

    const standaloneParams = buildStandaloneSearchParams({
      flights,
      occupants,
      fareType,
      fareCabin,
    })
    const firstLeg = itinerary[0]
    const lastLeg = itinerary[itinerary.length - 1]
    const originAirportCode = standaloneParams?.originAirportCode ?? firstLeg?.departureAirport
    const destinationAirportCode = standaloneParams?.destinationAirportCode ?? lastLeg?.arrivalAirport

    const departureDate = standaloneParams?.departDate ?? firstLeg?.departureDate
    const returnDate = standaloneParams?.returnDate ?? undefined

    if (originAirportCode && destinationAirportCode) {
      persistRecentSearch(buildFlightRecentSearchUpsertBody({
        tripStopId,
        originAirportCode,
        originAirportName: standaloneParams?.originAirportName ?? firstLeg?.departureAirport,
        destinationAirportCode,
        destinationAirportName: standaloneParams?.destinationAirportName ?? lastLeg?.arrivalAirport,
        departureDate,
        returnDate,
        fareType: standaloneParams?.fareType ?? fareType?.value,
        fareClass: standaloneParams?.fareClass ?? (
          fareCabin?.value ? fareClassToUrlMap[fareCabin.value] ?? 'economy' : 'economy'
        ),
        occupants,
      }))
      if (isExplicitSubmit && departureDate) {
        const searchParams = { originAirportCode, destinationAirportCode, departureDate, returnDate }
        eventBus?.dispatch({
          type: 'trip_planner_surface_activity',
          entries: [buildFlightSearchSubmittedActivityEntry(searchParams)],
        })
        eventBus?.dispatch({
          type: 'trip_planner_committed_state',
          entries: [buildLastFlightSearchCommittedStateEntry(tripStopId, searchParams)],
        })
      }
    }

    setSearchVersion(nextVersion)
    setSearchParams(params)
    setHasSearched(true)
    setActiveSegmentKey(
      getSegmentKey(0, fareType?.value === FlightsFareTypes.RETURN),
    )
    setSelectedFlights({})
    setSelectedFareFamilies({})
    setIsDetailsDrawerOpen(false)
    setDetailsJourney(undefined)
  }, [eventBus, fareCabin, fareType, flights, occupants, persistRecentSearch, searchVersion, tripStopId])

  // Explicit customer submits only -- see handleSearch's isExplicitSubmit param. Passed to both
  // the desktop widget's onSearchAirport and the mobile form's onSubmit below.
  const handleExplicitSearch = useCallback(() => {
    handleSearch(true)
  }, [handleSearch])

  const hasAutoSearched = useRef(false)
  useEffect(() => {
    if (autoSearch && !hasAutoSearched.current) {
      hasAutoSearched.current = true
      handleSearch()
    }
  }, [autoSearch, handleSearch])

  const {
    journeys,
    fetching,
    error,
    searchResultsTimestamp,
    key: searchKey,
  } = useSearchFlights(searchParams ?? EMPTY_SEARCH_PARAMS, {
    disabled: !searchParams,
    segments,
    activeSegmentKey: activeSegment,
    selectedFlights,
    selectedFareFamilies,
    isStandalone: canRequestLuxPlusFlightPricing,
  })

  const cachedSearchId = useAppSelector(
    (state) => state.flights.searchV2Flights[searchKey]?.searchId,
  )
  const cachedSessionId = useAppSelector(
    (state) => state.flights.searchV2Flights[searchKey]?.sessionId,
  )

  const displayJourneys = useChatFlightResultsSorting({
    journeys,
    activeSegmentKey: activeSegment,
    sort: flightSort,
    departDate,
    returnDate,
    selectedFlights,
  })

  // Filter the sorted, same-day-aware results. Filter options are built from
  // `displayJourneys` so they stay stable as the user narrows the selection;
  // filters reset whenever the search or active segment changes.
  const tripFlightFilters = useTripFlightFilters(displayJourneys, {
    resetKey: `${searchKey}-${activeSegment}`,
  })
  const { visibleJourneys, resetAllFilters } = tripFlightFilters

  const expiryTimestamp = useMemo(() => {
    if (!searchResultsTimestamp) return 0
    return moment(searchResultsTimestamp)
      .add(FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES, 'minutes')
      .toDate()
      .getTime()
  }, [searchResultsTimestamp])

  useEffect(() => {
    if (searchResultsTimestamp) {
      resetFlightsTimer(expiryTimestamp)
    }
  }, [expiryTimestamp, searchResultsTimestamp])

  const handleSearchRef = useRef(handleSearch)
  handleSearchRef.current = handleSearch

  useEffect(() => {
    if (!hasSearched || fetching || !expiryTimestamp) return

    if (hasFlightSessionExpired()) {
      handleSearchRef.current()
      return
    }

    const remaining = expiryTimestamp - Date.now()
    if (remaining <= 0) {
      handleSearchRef.current()
      return
    }

    const timeout = setTimeout(() => {
      handleSearchRef.current()
    }, remaining)

    return () => clearTimeout(timeout)
  }, [hasSearched, fetching, expiryTimestamp])

  const currentSegmentIndex = useMemo(() => {
    return Math.max(
      0,
      segments.findIndex((s) => s === activeSegment),
    )
  }, [segments, activeSegment])

  const handleStepClick = useCallback<StepClickHandler>(
    (stepIndex) => {
      setActiveSegmentKey(segments[stepIndex])
      setIsDetailsDrawerOpen(false)
      setDetailsJourney(undefined)
      // Keep the clicked step's own selection; only clear the legs after it (their
      // choices may depend on this leg, and the user is re-opening this step to
      // review/change it, not to lose it).
      const keysToRemove = segments.slice(stepIndex + 1)
      setSelectedFlights((prev) => {
        const next = { ...prev }
        for (const key of keysToRemove) delete next[key]
        return next
      })
      setSelectedFareFamilies((prev) => {
        const next = { ...prev }
        for (const key of keysToRemove) delete next[key]
        return next
      })
    },
    [segments],
  )

  const handleViewDetails = useCallback((journey: App.JourneyV2) => {
    setDetailsJourney(journey)
    setIsDetailsDrawerOpen(true)
  }, [])

  const handleDetailsClose = useCallback(() => {
    setIsDetailsDrawerOpen(false)
  }, [])

  const handleDetailsAfterClose = useCallback(() => {
    setDetailsJourney(undefined)
  }, [])

  const getStepSublabel = useCallback(
    (journey: App.JourneyV2) => {
      const flight = journey.flightGroup.flights[0]
      const date = dateFormatter(flight.departureDateTime, INTL_DAY_MONTH_NAME)
      return `${date} · ${flight.departingTime}`
    },
    [dateFormatter],
  )

  const handleSelectFlight = useCallback(
    async(data: { journey: App.JourneyV2 }) => {
      const currentSegmentKey = activeSegment
      const currentSegmentIndex = segments.findIndex(
        (key) => key === currentSegmentKey,
      )

      if (currentSegmentIndex < segments.length - 1) {
        setSelectedFlights((prev) => ({
          ...prev,
          [currentSegmentKey]: data.journey,
        }))
        setActiveSegmentKey(segments[currentSegmentIndex + 1])
        setIsDetailsDrawerOpen(false)
        return
      }

      const departingJourney = selectedFlights.departing
      const returningJourney = isReturn ? data.journey : undefined
      const selectedJourneyList = [
        ...segments.slice(0, -1).map((segment) => selectedFlights[segment]),
        data.journey,
      ].filter((journey): journey is App.JourneyV2 => !!journey)

      const outbound =
        selectedJourneyList[0] ?? departingJourney ?? data.journey
      const outboundFlights = outbound.flightGroup.flights
      const from = outboundFlights[0].departureAirport
      const finalJourney =
        selectedJourneyList[selectedJourneyList.length - 1] ?? outbound
      const finalFlights = finalJourney.flightGroup.flights
      const to = finalFlights[finalFlights.length - 1].arrivalAirport

      const cacheSelectedJourneys = () => {
        let journeysToCache: Array<App.JourneyV2> = [data.journey]
        if (isMultiCity) {
          journeysToCache = selectedJourneyList
        } else if (isReturn && departingJourney && returningJourney) {
          journeysToCache = [departingJourney, returningJourney]
        }
        // The drawer search requests LuxPlus pricing for charge-eligible members
        // (isStandalone gate), so cache journeys under that same mode. Charge-path
        // reads (getCachedJourney with the gate) then reuse the member searchId; a
        // non-member's public journeys are cached (and reused) as public.
        cacheShortlistedJourneys(
          journeysToCache,
          cachedSearchId,
          cachedSessionId,
          canRequestLuxPlusFlightPricing,
        )
      }

      if (isCartMode) {
        if (
          !onCartFlightSelect ||
          !searchParams ||
          isCartSubmitting ||
          isStreaming
        ) {
          return
        }
        const didSelect = await Promise.resolve(
          onCartFlightSelect({
            departingJourney: outbound,
            returningJourney,
            multiCityJourneys: isMultiCity ? selectedJourneyList : undefined,
            searchParams,
            searchId: cachedSearchId,
            sessionId: cachedSessionId,
            searchExpiry: expiryTimestamp,
          }),
        ).catch(() => false)
        if (didSelect !== false) {
          cacheSelectedJourneys()
          setDetailsJourney(undefined)
          onClose()
        }
        return
      }

      if (!onSendMessage || isStreaming) return

      // Ask for the fare before the message goes out, so the assistant receives
      // the flight and the chosen fare together and the traveller is never sent
      // back to the cart to answer the same question a second time.
      const fareResult = await selectFlightFareFamilies({
        departingJourney: outbound,
        returningJourney,
        occupants: searchParams?.occupancies[0] ?? { adults: 1 },
        searchId: cachedSearchId,
        sessionId: cachedSessionId,
        showModal,
        isMultiCity,
      })
      if (fareResult.status !== 'selected') return
      const { departingFareFamily, returningFareFamily } = fareResult.selection

      cacheSelectedJourneys()

      let userMessage: string
      let contextContent: string

      if (isMultiCity) {
        const groupCurrency = selectedJourneyList[0]?.currencyCode
        const combinedTotal = selectedJourneyList.reduce(
          (sum, journey) => sum + (journey.price.adult.totalFare ?? 0),
          0,
        )
        const message = buildMultiCityFlightMessage({
          intl,
          from,
          to,
          selectedJourneyList,
          groupCurrency,
          combinedTotal,
          buildFlightContext,
        })
        userMessage = message.userMessage
        contextContent = message.contextContent
      } else if (isReturn && departingJourney && returningJourney) {
        userMessage = intl.formatMessage(
          {
            id: 'ZE1Bw2jDpdHXdHmw312L',
            defaultMessage: 'Add return flight from {from} to {to}',
            description:
              'User chat message shown when adding a return flight from the flight search drawer',
          },
          {
            from,
            to,
          },
        )
        contextContent = `Departing flight:\n${buildFlightContext(
          departingJourney,
          departingFareFamily,
        )}\n\nReturning flight:\n${buildFlightContext(returningJourney, returningFareFamily)}`
      } else {
        userMessage = intl.formatMessage(
          {
            id: 'mtc-4uBZ7D3tkBZV8ueR',
            defaultMessage: 'Add flight from {from} to {to}',
            description:
              'User chat message shown when adding a one-way flight from the flight search drawer',
          },
          {
            from,
            to,
          },
        )
        contextContent = buildFlightContext(data.journey, departingFareFamily)
      }

      const searchMeta = [
        `Search expiry: ${expiryTimestamp}`,
        `Search params: ${searchParams ? JSON.stringify(searchParams) : ''}`,
      ].join('\n')

      const message = `${userMessage}\n<Context label="Flight search result">${contextContent}\n\n${searchMeta}</Context>`
      onSendMessage(message)
      setDetailsJourney(undefined)
      onClose()
    },
    [
      intl,
      segments,
      activeSegment,
      selectedFlights,
      isReturn,
      isMultiCity,
      isCartMode,
      isCartSubmitting,
      onCartFlightSelect,
      onSendMessage,
      isStreaming,
      onClose,
      expiryTimestamp,
      searchParams,
      cachedSearchId,
      cachedSessionId,
      canRequestLuxPlusFlightPricing,
      showModal,
    ],
  )

  const savedHeading = intl.formatMessage({
    id: 'UkCP7iQhTsHWmenEq93v',
    defaultMessage: 'Saved',
    description:
      'Section heading for the saved flights in the trip planner flight drawer',
  })

  const exploreHeading = intl.formatMessage({
    id: 'hn-UDYbNIkT2Q6CwUXBK',
    defaultMessage: 'Explore more',
    description:
      'Section heading above the flight search in the trip planner flight drawer',
  })

  const noResultsMessage = intl.formatMessage({
    id: 'P51jHD0yS8A8bb49PJWq',
    defaultMessage:
      'No flights found for your search. Try different dates or airports.',
    description:
      'Message shown when no flight results are found in the trip planner drawer',
  })

  const selectFlightLabel = currentSegmentIndex === segments.length - 1
    ? intl.formatMessage({
        id: '_r0LZ1-gKOSRCr2u9yIC',
        defaultMessage: 'Book now',
        description: 'Button label to confirm the final flight selection in the trip planner drawer',
      })
    : intl.formatMessage({
        id: 'FpuEHM82GUVuDqZ0SICN',
        defaultMessage: 'Select',
        description: 'Button label to choose a flight before selecting the next trip segment',
      })

  const departingFlightLabel = intl.formatMessage({
    id: 'qJfCIiKntFNv9yNLHAHJ',
    defaultMessage: 'Departing flight',
    description: 'Label for departing flight segment in trip planner drawer',
  })

  const returningFlightLabel = intl.formatMessage({
    id: '4p1QhxNAMtAdkUW9nReN',
    defaultMessage: 'Returning flight',
    description: 'Label for returning flight segment in trip planner drawer',
  })

  const resultsHeaderLabel = useMemo(() => {
    if (isMultiCity) {
      return intl.formatMessage(
        {
          id: 'xmce4KMu4_6CYz9hwEpo',
          defaultMessage: 'Flight {flightNumber}',
          description:
            'Label for a multi-city flight segment with its sequence number',
        },
        { flightNumber: currentSegmentIndex + 1 },
      )
    }
    if (isReturn && isReturningStep) return returningFlightLabel
    return departingFlightLabel
  }, [
    intl,
    isMultiCity,
    isReturn,
    isReturningStep,
    currentSegmentIndex,
    departingFlightLabel,
    returningFlightLabel,
  ])

  const showResults = hasSearched && !fetching && displayJourneys.length > 0
  const showNoResults =
    hasSearched && !fetching && !error && displayJourneys.length === 0
  const showError = hasSearched && !fetching && !!error
  const showSavedFlightsSection = hasSavedFlights && !!headerSlot
  const flightContextIntentParams = useMemo(
    () => buildFlightSearchIntentParams({
      flights,
      occupants,
      fareType,
      fareCabin,
    }),
    [fareCabin, fareType, flights, occupants],
  )
  const flightContextLabel = useMemo(
    () => buildFlightSearchContextLabel(searchParams ?? flightContextIntentParams),
    [flightContextIntentParams, searchParams],
  )
  const flightContextDetails = useMemo(
    () => buildFlightSearchDrawerContext({
      intentParams: flightContextIntentParams,
      searchParams,
      hasSearched,
      fetching,
      hasError: !!error,
      activeSegmentLabel: resultsHeaderLabel,
      sort: flightSort,
      activeFilterCount: tripFlightFilters.activeFilterCount,
      displayJourneys,
      visibleJourneys,
      selectedFlights,
      searchId: cachedSearchId,
      sessionId: cachedSessionId,
      searchExpiry: expiryTimestamp || undefined,
    }),
    [
      flightContextIntentParams,
      searchParams,
      hasSearched,
      fetching,
      error,
      resultsHeaderLabel,
      flightSort,
      tripFlightFilters.activeFilterCount,
      displayJourneys,
      visibleJourneys,
      selectedFlights,
      cachedSearchId,
      cachedSessionId,
      expiryTimestamp,
    ],
  )
  return (
    <>
      <TripDrawerAssistantBridge
        presentation={drawerPresentation}
        isOpen
        contextLabel={flightContextLabel}
        contextDetails={flightContextDetails}
      />

      <SearchWrapper>
        <VerticalSpacer gap={24}>
          {showSavedFlightsSection && (
            <VerticalSpacer gap={12}>
              <Heading variant="heading4">{savedHeading}</Heading>
              {headerSlot}
            </VerticalSpacer>
          )}

          <VerticalSpacer gap={16}>
            {showSavedFlightsSection && (
              <Heading variant="heading4">{exploreHeading}</Heading>
            )}

            <CSSBreakpoint min="tablet">
              <FlightSearchWidgetDesktop
                onSearchAirport={handleExplicitSearch}
                showMultiCity={showMultiCity}
                groupAirportDropdowns
                initialFocus={isDesktopSearchWidgetActive ? desktopInitialFocus : undefined}
              />
            </CSSBreakpoint>
            <CSSBreakpoint max="mobile">
              <MobileSearchForm
                gap={12}
                as="form"
                onSubmit={(e: React.FormEvent) => {
                  e.preventDefault()
                  handleExplicitSearch()
                }}
              >
                <FlightSearchWidgetMobile
                  showMultiCity={showMultiCity}
                  initialFocus={isDesktopSearchWidgetActive ? undefined : mobileInitialFocus}
                />
                <MobileSearchButton
                  kind="primary"
                  size="small"
                  type="submit"
                >
                  {intl.formatMessage({
                    id: 'WGRNTZqigCaFkyNGLUr0',
                    defaultMessage: 'Search',
                    description:
                      'Search button label to initiate flight search in the trip planner drawer',
                  })}
                </MobileSearchButton>
              </MobileSearchForm>
            </CSSBreakpoint>
          </VerticalSpacer>
        </VerticalSpacer>
      </SearchWrapper>

      {hasSearched && searchResultsTimestamp && (
        <CountdownWrapper>
          <FlightSessionCountdownClock />
        </CountdownWrapper>
      )}

      {hasSearched && (isReturn || isMultiCity) && searchParams && (
        <ContentWrapper>
          <StepperProgressSteps activeStepIndex={currentSegmentIndex}>
            {searchParams.itinerary.map((leg, index) => (
              <StepperProgressStep
                key={segments[index]}
                stepIndex={index}
                label={intl.formatMessage(
                  {
                    id: 'AEiFGqvrHSc9lXaMfYat',
                    defaultMessage: '{origin} — {destination}',
                    description:
                      'Flight route step label showing origin and destination airport codes in trip planner',
                  },
                  {
                    origin: leg.departureAirport,
                    destination: leg.arrivalAirport,
                  },
                )}
                onStepClick={
                  selectedFlights[segments[index]] ? handleStepClick : undefined
                }
                sublabel={
                  selectedFlights[segments[index]]
                    ? getStepSublabel(selectedFlights[segments[index]])
                    : undefined
                }
              />
            ))}
          </StepperProgressSteps>
        </ContentWrapper>
      )}

      {fetching && (
        <>
          <FlightsLoadingTopBar />
          <ContentWrapper>
            <LoadingAnimationBox variant="flights" />
          </ContentWrapper>
        </>
      )}

      {showError && (
        <ContentWrapper>
          <BodyText variant="medium" colour="critical">
            {intl.formatMessage({
              id: 'W6e3tDO59GW7SKXKCf3P',
              defaultMessage:
                'Something went wrong while searching for flights. Please try again.',
              description:
                'Error message when flight search fails in the trip planner drawer',
            })}
          </BodyText>
        </ContentWrapper>
      )}

      {showNoResults && (
        <ContentWrapper>
          <BodyText variant="medium" colour="light-foreground-alt">
            {noResultsMessage}
          </BodyText>
        </ContentWrapper>
      )}

      {showResults && (
        <ContentWrapper>
          <VerticalSpacer gap={12}>
            <ChatFlightResultsHeader
              label={resultsHeaderLabel}
              count={visibleJourneys.length}
              sort={flightSort}
              originAirportCode={
                searchParams?.itinerary[currentSegmentIndex]?.departureAirport
              }
              destinationAirportCode={
                searchParams?.itinerary[currentSegmentIndex]?.arrivalAirport
              }
              onSortChange={setFlightSort}
            />

            <TripFlightFiltersPanel
              filterControls={tripFlightFilters}
              originAirportCode={
                searchParams?.itinerary[currentSegmentIndex]
                  ?.departureAirport ?? ''
              }
              destinationAirportCode={
                searchParams?.itinerary[currentSegmentIndex]?.arrivalAirport ??
                ''
              }
            />

            {visibleJourneys.length === 0 && (
              <VerticalSpacer gap={12}>
                <BodyText variant="medium" colour="light-foreground-alt">
                  {intl.formatMessage({
                    id: 'tHI6ugJkg6Xo9BikjkNq',
                    defaultMessage: 'No flights match your filters.',
                    description:
                      'Message shown when active filters exclude all flight results in the trip planner drawer',
                  })}
                </BodyText>
                <TextButton
                  kind="secondary"
                  size="small"
                  onClick={resetAllFilters}
                >
                  {intl.formatMessage({
                    id: 'drpXi_9Ybwre8YQMfj49',
                    defaultMessage: 'Reset filters',
                    description:
                      'Button label to clear flight filters when no results match in the trip planner drawer',
                  })}
                </TextButton>
              </VerticalSpacer>
            )}

            {visibleJourneys.map((journey) => (
              <FlightSearchResultCard
                key={journey.id}
                journey={journey}
                onSelect={canSelectFlight ? handleSelectFlight : undefined}
                isAlreadyAdded={isJourneyAlreadyAdded(
                  journey,
                  existingFlightFingerprints,
                )}
                isCartMode={isCartMode}
                isSelectDisabled={isResultSelectionDisabled}
                selectLabel={shouldUseMobileDetailsDrawer ? selectFlightLabel : undefined}
                showSelectButton={canSelectFlight}
                onViewDetails={shouldUseMobileDetailsDrawer ? handleViewDetails : undefined}
              />
            ))}
          </VerticalSpacer>
        </ContentWrapper>
      )}
      {detailsJourney && (
        <FlightJourneyDetailsDrawer
          isOpen={isDetailsDrawerOpen}
          journey={detailsJourney}
          onClose={handleDetailsClose}
          onAfterClose={handleDetailsAfterClose}
          onSelect={canSelectFlight ? handleSelectFlight : undefined}
          selectLabel={selectFlightLabel}
          showSelectButton={canSelectFlight}
          isSelectDisabled={isCartSubmitting || isStreaming}
          isAlreadyAdded={isJourneyAlreadyAdded(
            detailsJourney,
            existingFlightFingerprints,
          )}
        />
      )}
    </>
  )
}

function FlightSearchDrawer({
  isOpen,
  onClose,
  originAirportCode,
  originAirportName,
  destinationAirportCode,
  destinationAirportName,
  departureDate,
  returnDate,
  indicativeSearchMonth,
  itinerary,
  fareType: fareTypeProp,
  fareClass: fareClassProp,
  occupants,
  passengers,
  autoSearch,
  onSendMessage,
  isStreaming = false,
  onCartFlightSelect,
  isCartSubmitting = false,
  existingFlights,
  headerSlot,
  hasSavedFlights,
  showMultiCity,
  tripStopId,
}: Props) {
  const intl = useIntl()
  const { fareTypes, fareCabins } = useFlightFareLabels()
  const drawerPresentation = useRightPanelTripDrawerPresentation()

  const drawerTitle = intl.formatMessage({
    id: '0py3lHxthzacj0uaxcjK',
    defaultMessage: 'Flights',
    description: 'Heading for the flight search drawer in trip planner',
  })
  const browseAllResultsLabel = intl.formatMessage({
    id: 'w3Ky3OJt1VCY3yhGrapK',
    defaultMessage: 'Browse all results',
    description: 'Button label to open the full flight search results page from the flight drawer',
  })

  const existingFlightFingerprints = useMemo(() => {
    const set = new Set<string>()
    if (!existingFlights) return set
    for (const option of existingFlights) {
      const data = option.savedItemData
      if (data?.source === 'le_flight' && data.flightNumber) {
        set.add(
          getFlightFingerprint(
            data.flightNumber,
            data.departureAirport,
            data.arrivalAirport,
          ),
        )
      }
    }
    return set
  }, [existingFlights])

  const validDepartureDate = getValidFlightSearchDate(departureDate)
  const validReturnDate = getValidFlightSearchDate(returnDate)
  const validItinerary = useMemo(
    () => itinerary?.map((leg) => ({
      ...leg,
      departureDate: getValidFlightSearchDate(leg.departureDate),
    })),
    [itinerary],
  )
  const resolvedCalendarFocusMonth = useMemo(
    () => resolveSearchCalendarFocusDate({
      relevantConcreteStart: validDepartureDate ?? validItinerary?.[0]?.departureDate,
      indicativeSearchMonth,
    }),
    [indicativeSearchMonth, validDepartureDate, validItinerary],
  )

  const widgetInitialState = useMemo((): FlightSearchWidgetState => {
    const requestedFareType = fareTypeProp
      ? fareTypes.find((ft) => ft.value === fareTypeProp)
      : undefined
    // A multi-city fare type needs the legs to go with it. Opening from a single
    // saved leg gives us one, and a one-leg multi-city search is rejected by the
    // search API, so show that leg as a one-way instead.
    const hasMultiCityItinerary = (validItinerary?.length ?? 0) > 1
    const resolvedFareType =
      requestedFareType?.value === FlightsFareTypes.MULTI_CITY && !hasMultiCityItinerary
        ? fareTypes.find((ft) => ft.value === FlightsFareTypes.ONE_WAY) ?? requestedFareType
        : requestedFareType
    const normalizedFareClass = fareClassProp
      ? parseFareClassFromUrl(fareClassProp) ?? fareClassProp
      : undefined
    const resolvedFareCabin = normalizedFareClass
      ? fareCabins.find((fc) => fc.value === normalizedFareClass)
      : undefined

    const state: FlightSearchWidgetState = {
      ...initialState,
      fareType:
        resolvedFareType ??
        fareTypes.find((ft) => ft.value === FlightsFareTypes.ONE_WAY) ??
        fareTypes[0],
      fareCabin: resolvedFareCabin ?? fareCabins[0],
      calendarFocusMonth: resolvedCalendarFocusMonth,
    }

    if (occupants) {
      state.occupants = {
        ...state.occupants,
        ...occupants,
      }
    } else if (passengers && passengers > 0) {
      state.occupants = {
        ...state.occupants,
        adults: passengers,
      }
    }

    if (
      validItinerary?.length &&
      resolvedFareType?.value === FlightsFareTypes.MULTI_CITY
    ) {
      state.flights = validItinerary?.map((leg) =>
        createEmptyFlightSearchWidgetFlightItem({
          id: uuidV4(),
          departureAirport: {
            airportCode: leg.departureAirport,
            airportName: leg.departureAirport,
          } as App.AirportLocation,
          arrivalAirport: {
            airportCode: leg.arrivalAirport,
            airportName: leg.arrivalAirport,
          } as App.AirportLocation,
          checkinDate: leg.departureDate ? moment(leg.departureDate) : undefined,
        }),
      ) ?? state.flights
      return state
    }

    if (originAirportCode) {
      state.flights = [
        {
          ...state.flights[0],
          id: uuidV4(),
          departureAirport: {
            airportCode: originAirportCode,
            airportName: originAirportName ?? originAirportCode,
          } as App.AirportLocation,
        },
      ]
    }

    if (destinationAirportCode && state.flights[0]) {
      state.flights = [
        {
          ...state.flights[0],
          arrivalAirport: {
            airportCode: destinationAirportCode,
            airportName: destinationAirportName ?? destinationAirportCode,
          } as App.AirportLocation,
        },
      ]
    }

    if (validDepartureDate) {
      state.flights = [
        {
          ...state.flights[0],
          checkinDate: moment(validDepartureDate),
        },
      ]
    }

    if (validReturnDate) {
      state.flights = [
        {
          ...state.flights[0],
          checkoutDate: moment(validReturnDate),
        },
      ]
    }

    return state
  }, [
    fareTypes,
    fareCabins,
    fareTypeProp,
    fareClassProp,
    occupants,
    passengers,
    validItinerary,
    originAirportCode,
    originAirportName,
    destinationAirportCode,
    destinationAirportName,
    validDepartureDate,
    validReturnDate,
    resolvedCalendarFocusMonth,
  ])

  const [widgetState, widgetDispatch] = useReducer<
    FlightSearchWidgetState,
    [FlightSearchWidgetDispatchAction]
  >(flightSearchWidgetStateReducer, widgetInitialState)
  // Sticky-mounted drawer: skip the mount-equal dispatch (initial state already
  // has this value) but keep syncing when indicative month arrives later.
  const hasSyncedCalendarFocusRef = useRef(false)
  useEffect(() => {
    if (!hasSyncedCalendarFocusRef.current) {
      hasSyncedCalendarFocusRef.current = true
      return
    }

    widgetDispatch({
      type: FlightSearchWidgetActions.SET_CALENDAR_FOCUS_MONTH,
      calendarFocusMonth: resolvedCalendarFocusMonth,
    })
  }, [resolvedCalendarFocusMonth])
  const desktopInitialFocus = useMemo(
    () => getInitialFlightSearchFocus(widgetInitialState, { includeDateFields: true }),
    [widgetInitialState],
  )
  const mobileInitialFocus = useMemo(
    () => getInitialFlightSearchFocus(widgetInitialState),
    [widgetInitialState],
  )
  const browseAllResultsUrl = useMemo(() => {
    if (onCartFlightSelect) {
      return undefined
    }

    const standaloneParams = buildStandaloneSearchParams(widgetState)
    return standaloneParams ? buildStandaloneFlightSearchUrl(standaloneParams) : undefined
  }, [onCartFlightSelect, widgetState])
  const headerActions = browseAllResultsUrl ? (
    <AssistChip
      variant="filled"
      size="medium"
      to={browseAllResultsUrl}
      endIcon={<LineExternalLinkAltIcon />}
    >
      {browseAllResultsLabel}
    </AssistChip>
  ) : undefined

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={onClose}
      direction="right-to-left"
      presentation={drawerPresentation}
      header={<TripDrawerHeaderTitle title={drawerTitle} />}
      headerActions={headerActions}
    >
      <FlightSearchWidgetStateProvider
        state={widgetState}
        dispatch={widgetDispatch}
      >
        <ModalProvider>
          <FlightSearchDrawerContent
            onSendMessage={onSendMessage}
            isStreaming={isStreaming}
            onCartFlightSelect={onCartFlightSelect}
            isCartSubmitting={isCartSubmitting}
            onClose={onClose}
            existingFlightFingerprints={existingFlightFingerprints}
            autoSearch={hasCompleteFlightSearchDefaults({
              originAirportCode,
              destinationAirportCode,
              departureDate: validDepartureDate,
              returnDate: validReturnDate,
              itinerary: validItinerary,
              fareType: fareTypeProp,
            }) && (autoSearch ?? true)}
            headerSlot={headerSlot}
            hasSavedFlights={hasSavedFlights}
            showMultiCity={showMultiCity}
            drawerPresentation={drawerPresentation}
            desktopInitialFocus={desktopInitialFocus}
            mobileInitialFocus={mobileInitialFocus}
            tripStopId={tripStopId}
          />
        </ModalProvider>
      </FlightSearchWidgetStateProvider>
    </TripDrawer>
  )
}

export default FlightSearchDrawer
